import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'NetSuite (SuiteQL)',
  icon: 'https://cdn.app.pliable.co/img/connectors/netsuite.svg',
  content: `
The NetSuite (SuiteQL) connector reads your NetSuite data through NetSuite's REST web services API using SuiteQL. It is **read-only**: Pliable never writes to NetSuite. Access is granted through a dedicated integration record and a role you control, and you can revoke it at any time.

## What gets synced

| Stream | Contents |
|---|---|
| \`customers\`, \`vendors\`, \`employees\` | Entity records, including custom fields |
| \`items\` | Inventory and non-inventory items, including matrix parent/child items and custom fields |
| \`accounts\`, \`locations\`, \`subsidiaries\`, \`classes\`, \`departments\`, \`currencies\`, \`price_levels\` | Lookup records |
| \`transactions\` | Headers for every transaction type (sales orders, invoices, cash sales, item fulfillments, return authorizations, credit memos, purchase orders, item receipts, vendor bills, journal entries). Filter on \`record.type\`. |
| \`transaction_lines\` | Line detail for each transaction |
| \`transaction_accounting_lines\` | General ledger impact per line |
| \`inventory_balances\` | Current on-hand by item, location, bin and status |
| \`deleted_records\` | NetSuite's deleted-record log, so deletions can be reflected downstream |

Records with a last-modified date sync incrementally. Lookup records and inventory balances are re-read in full each run. Each row's full set of fields, including custom fields, arrives in a single \`record\` column so nothing is lost if your account has custom fields we have not seen before. Streams that your account or the integration role cannot access (for example \`subsidiaries\` in an account without OneWorld) can be deselected.

## What you need to provide

Three values from NetSuite, plus the certificate Pliable generates for you:

| Value | Where it comes from |
|---|---|
| **Account ID** | Setup → Company → Company Information. Sandbox accounts look like \`1234567_SB1\`. |
| **OAuth 2.0 Client ID** | The integration record (step 3 below). NetSuite shows it once, when you save. |
| **Certificate ID** | The OAuth 2.0 Client Credentials (M2M) setup page, after you upload the certificate (step 5). |

## Setup (OAuth 2.0 machine-to-machine, recommended)

A NetSuite **Administrator** needs to do steps 1 to 5.

1. **Enable the features.** Go to Setup → Company → Enable Features, open the **SuiteCloud** tab, and turn on **REST Web Services** and **OAuth 2.0**.

2. **Create a read-only role and user.** Create a dedicated role for Pliable, then create a dedicated user (for example "Pliable Integration") that has only this role. Do not use the Administrator role. **A new role can read nothing until you grant it**, so add every permission below, each at **View** (never Edit or Full), on the role's Permissions tab. See "Permissions to grant" below.

3. **Create the integration record.** Go to Setup → Integration → Manage Integrations → New. Name it "Pliable", turn on **Client Credentials (Machine to Machine) Grant**, and leave the other authentication options off. Save, then copy the **Client ID**. NetSuite only shows it once.

4. **Generate the certificate in Pliable.** In Pliable, add a NetSuite (SuiteQL) source, enter the Account ID and Client ID, and save. In the **Certificate** section, click **Generate certificate**, then copy it or download the \`.pem\` file. The private key stays in Pliable and is never displayed.

5. **Upload the certificate to NetSuite.** Go to Setup → Integration → Manage Authentication → OAuth 2.0 Client Credentials (M2M) Setup → Create New. Choose the integration record, the integration user and the role from steps 2 and 3, and upload the certificate. NetSuite shows a **Certificate ID**.

6. **Activate it.** Paste the Certificate ID into Pliable and click **Verify & activate**. Pliable requests a token and runs a test query before it saves anything, so a typo cannot break an existing connection. Once it succeeds, choose your streams, click **Check record access** to confirm the role can read each one, and run a sync.

## Permissions to grant

NetSuite roles are deny-by-default and grant access one record type at a time. If the role is missing a record's permission, NetSuite does not say "access denied". It says the record "was not found". Pliable only syncs what you grant.

**Required for every stream** (Setup and Reports tabs):

| Permission | Why |
|---|---|
| REST Web Services | Lets Pliable call the API |
| Log in using OAuth 2.0 Access Tokens | Lets the role authenticate with the certificate |
| SuiteAnalytics Workbook | Lets the role run SuiteQL queries |

**Required per stream** (View level). Grant only what you want to sync, and deselect the other streams in Pliable:

| Stream | Permission to grant (tab) |
|---|---|
| \`customers\` | Customers (Lists) |
| \`vendors\` | Vendors (Lists) |
| \`employees\` | Employees (Lists) |
| \`items\` | Items (Lists) |
| \`accounts\` | Accounts (Lists) |
| \`locations\` | Locations (Lists) |
| \`subsidiaries\` | Subsidiaries (Lists). OneWorld accounts only. |
| \`classes\` | Classes (Lists) |
| \`departments\` | Departments (Lists) |
| \`currencies\` | Currency (Lists). Needs the Multiple Currencies feature. |
| \`transactions\`, \`transaction_lines\`, \`transaction_accounting_lines\` | One permission for each transaction type you sync: Sales Order, Invoice, Cash Sale, Item Fulfillment, Return Authorization, Credit Memo, Purchase Order, Item Receipt, Vendor Bill, Journal Entry (Transactions) |
| \`inventory_balances\` | Items (Lists) and the inventory features enabled in your account |
| \`deleted_records\` | Search for Deleted Records (Lists) |

Permission names can differ slightly between NetSuite versions and account features. After activating, click **Check record access** in Pliable: it shows which selected streams the role can read, so you know exactly what is still missing.

## Rotating the certificate

Certificates in NetSuite last at most two years. Pliable shows the expiry date and warns you 30 days before it. To rotate without any downtime:

1. Click **Rotate certificate**. Your current certificate keeps working.
2. Upload the new certificate in NetSuite (a single integration can have several certificates at once) and copy its Certificate ID.
3. Paste the new Certificate ID and click **Verify & activate**. Pliable switches over only if verification succeeds.
4. Revoke the old certificate in NetSuite whenever you like.

Each certificate is tied to one combination of integration record, role and user, so if you change the role or user you need a new certificate.

## Token-based authentication (alternative)

If you cannot set up certificates, choose **tba** as the authentication method. Create an integration record with Token-Based Authentication enabled, create an access token for the integration user and role, and enter the consumer key, consumer secret, token ID and token secret. The role requirements are the same as above.

## Troubleshooting

- **"NetSuite rejected the credentials"** during activation: check that the Account ID, Client ID and Certificate ID are all from the same NetSuite account, and that the certificate you uploaded is the one currently shown in Pliable.
- **"Authenticated, but a SuiteQL query failed":** the role is missing a connection permission. Confirm REST Web Services and SuiteAnalytics Workbook.
- **"This role cannot read any records":** none of the record permissions are granted yet. See Permissions to grant.
- **"Could not reach NetSuite":** the Account ID is probably wrong. Sandbox IDs include the \`_SB1\` suffix.
- **A stream fails with a 400 error, or "Record '…' was not found":** the role cannot see that record type, or the feature is not enabled in your account. Add View permission for that record (for example Currency under Lists) or deselect the stream.
- **Missing or empty data:** SuiteQL only returns records the role has View permission on.
`,
};

export default doc;
